// Global Instructions Rule Applied!
//
// Flattens the Income Statement / Balance Sheet row stream (view__fund_statement)
// into the grid the Statements tab exports to Excel: one row per figure, one
// column per statement column.
//
// A header row carries no figure, so it becomes a column instead: a section
// header is the row's Section, and the header of a group printed with its
// lines (Professional fees) is the Group of the rows beneath it - nested
// groups join with ' / '. The group's own subtotal row keeps that Group.
import type { FundStatementColumn, FundStatementRow } from '../../../state_manager/data_models/fund_statements.ts'

export interface StatementExport {
  data: Record<string, unknown>[]
  columns: string[]
}

export function flattenStatementRows(rows: FundStatementRow[], columns: FundStatementColumn[]): StatementExport {
  // a statement read before any column was configured carries one amount
  const amountColumns = columns.length > 0 ? columns : [{ code: '', label: 'Amount' }]
  // open groups, outermost first: the indent their header sat on and its label
  let groups: Array<{ indent: number; label: string }> = []
  const data: Record<string, unknown>[] = []

  rows.forEach((row) => {
    // a row at a group's indent or shallower closes it - except the group's
    // own subtotal, which sits on the header's indent and closes it AFTER
    // being written
    const closesAfter = row.prog__row_kind === 'subtotal'
    groups = groups.filter((g) => g.indent < row.prog__indent || (closesAfter && g.indent === row.prog__indent))

    if (row.prog__row_kind === 'header') {
      if (row.prog__indent > 0) groups.push({ indent: row.prog__indent, label: row.prog__label })
      return
    }

    const flat: Record<string, unknown> = {
      Section: row.cfg_acc_statement_sections__label,
      Group: groups.map((g) => g.label).join(' / '),
      Code: row.acc_gl_accounts__account_code ?? '',
      Line: row.prog__label
    }
    amountColumns.forEach((col) => {
      flat[col.label] = (col.code ? row.prog__amounts?.[col.code] : row.prog__amount) ?? 0
    })
    data.push(flat)

    if (closesAfter) groups = groups.filter((g) => g.indent < row.prog__indent)
  })

  return { data, columns: ['Section', 'Group', 'Code', 'Line', ...amountColumns.map((c) => c.label)] }
}
